// WholesalePODetail.js — single wholesale order: header, status, line items.
// Tracking/workflow only — NO inventory_transactions are ever written here
// (units are pre-removed from Shopify; the daily sync handles inventory_on_hand).

import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { ArrowLeft, Plus, X, Trash2, Clock, Edit2, Check } from 'lucide-react'
import { format } from 'date-fns'
import ProductPicker from '../components/ProductPicker'

const STATUS_BADGE = { draft: 'neutral', confirmed: 'accent', shipped: 'info', closed: 'success', cancelled: 'danger' }
const STATUSES = ['draft', 'confirmed', 'shipped', 'closed', 'cancelled']
const STATUS_ORDER = { draft: 0, confirmed: 1, shipped: 2, closed: 3, cancelled: 4 }

export default function WholesalePODetail() {
  const { id } = useParams()
  const { activeBrand } = useAuth()
  const navigate = useNavigate()
  const [order, setOrder] = useState(null)
  const [lines, setLines] = useState([])
  const [products, setProducts] = useState([])
  const [customers, setCustomers] = useState([])
  const [lineModal, setLineModal] = useState(false)
  const [lineForm, setLineForm] = useState({ product_id: '', quantity: '', unit_price: '', shopify_already_deducted: true, notes: '' })
  const [saving, setSaving] = useState(false)
  const [editingHeader, setEditingHeader] = useState(false)
  const [headerForm, setHeaderForm] = useState({})

  useEffect(() => { load() }, [id, activeBrand])

  async function load() {
    const [{ data: orderData }, { data: lineData }, { data: prodData }, { data: custData }] = await Promise.all([
      supabase.from('wholesale_orders').select('*, wholesale_customers(name)').eq('id', id).single(),
      supabase.from('wholesale_order_lines').select('*, products(name, sku)').eq('wholesale_order_id', id),
      activeBrand ? supabase.from('products').select('id, name, sku, retail_price').eq('brand_id', activeBrand.id).is('deleted_at', null).order('sku') : Promise.resolve({ data: [] }),
      activeBrand ? supabase.from('wholesale_customers').select('id, name').eq('brand_id', activeBrand.id).order('name') : Promise.resolve({ data: [] })
    ])
    setOrder(orderData)
    setLines(lineData || [])
    setProducts(prodData || [])
    setCustomers(custData || [])
  }

  async function updateStatus(status) {
    await supabase.from('wholesale_orders').update({ status, updated_at: new Date().toISOString() }).eq('id', id)
    load()
  }

  function startEditHeader() {
    setHeaderForm({
      customer_id: order.customer_id || '',
      order_number: order.order_number || '',
      ship_date: order.ship_date || '',
      notes: order.notes || ''
    })
    setEditingHeader(true)
  }

  async function saveHeader() {
    await supabase.from('wholesale_orders').update({
      customer_id: headerForm.customer_id || null,
      order_number: headerForm.order_number,
      ship_date: headerForm.ship_date || null,
      notes: headerForm.notes || null,
      updated_at: new Date().toISOString()
    }).eq('id', id)
    setEditingHeader(false)
    load()
  }

  function handleProductSelect(pid) {
    const prod = products.find(p => p.id === pid)
    setLineForm(f => ({ ...f, product_id: pid, unit_price: f.unit_price || prod?.retail_price || '' }))
  }

  async function addLine() {
    setSaving(true)
    await supabase.from('wholesale_order_lines').insert({
      wholesale_order_id: id,
      product_id: lineForm.product_id || null,
      quantity: parseInt(lineForm.quantity, 10),
      unit_price: lineForm.unit_price ? parseFloat(lineForm.unit_price) : null,
      shopify_already_deducted: lineForm.shopify_already_deducted,
      notes: lineForm.notes || null
    })
    setSaving(false); setLineModal(false)
    setLineForm({ product_id: '', quantity: '', unit_price: '', shopify_already_deducted: true, notes: '' })
    load()
  }

  async function updateLine(lid, patch) {
    await supabase.from('wholesale_order_lines').update(patch).eq('id', lid)
    load()
  }

  async function deleteLine(lid) {
    if (window.confirm('Remove this line item?')) { await supabase.from('wholesale_order_lines').delete().eq('id', lid); load() }
  }

  const totalUnits = lines.reduce((s, l) => s + Number(l.quantity || 0), 0)
  const totalValue = lines.reduce((s, l) => s + (Number(l.quantity || 0) * Number(l.unit_price || 0)), 0)

  if (!order) return <div className="loading-screen"><div className="spinner" /></div>

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/wholesale-orders')}><ArrowLeft size={14} />Back</button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h1 style={{ fontFamily: 'var(--mono)' }}>{order.order_number}</h1>
              <select className="select" style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }} value={order.status} onChange={e => updateStatus(e.target.value)}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '2px' }}>{order.wholesale_customers?.name || 'No buyer set'}</div>
          </div>
        </div>
        {!editingHeader && <button className="btn btn-secondary btn-sm" onClick={startEditHeader}><Edit2 size={13} />Edit Details</button>}
      </div>

      {editingHeader ? (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600 }}>Edit Order Details</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditingHeader(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={saveHeader}><Check size={13} />Save</button>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="form-grid">
              <div className="form-group"><label className="form-label">Order Number</label><input className="input" value={headerForm.order_number} onChange={e => setHeaderForm(f => ({ ...f, order_number: e.target.value }))} /></div>
              <div className="form-group"><label className="form-label">Buyer</label>
                <select className="select" value={headerForm.customer_id} onChange={e => setHeaderForm(f => ({ ...f, customer_id: e.target.value }))}>
                  <option value="">— None —</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group"><label className="form-label">Ship Date</label><input className="input" type="date" value={headerForm.ship_date} onChange={e => setHeaderForm(f => ({ ...f, ship_date: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Notes</label><textarea className="textarea" rows={2} value={headerForm.notes} onChange={e => setHeaderForm(f => ({ ...f, notes: e.target.value }))} /></div>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
            {[
              { label: 'Buyer', value: order.wholesale_customers?.name || '—' },
              { label: 'Ship Date', value: order.ship_date ? format(new Date(order.ship_date + 'T12:00:00'), 'MMM d, yyyy') : '—' },
              { label: 'Total Units', value: totalUnits.toLocaleString() },
              { label: 'Order Value', value: totalValue > 0 ? `$${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—' },
            ].map(({ label, value }) => (
              <div className="card" key={label} style={{ padding: '14px 16px' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>{label}</div>
                <div style={{ fontSize: '14px', fontWeight: 500 }}>{value}</div>
              </div>
            ))}
          </div>
          {order.notes && (
            <div className="card" style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: '10px', color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Notes</div>
              <p style={{ fontSize: '13px', color: 'var(--text-2)' }}>{order.notes}</p>
            </div>
          )}
        </>
      )}

      {/* Status Timeline */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <Clock size={15} style={{ color: 'var(--accent)' }} />
          <h3 style={{ fontSize: '14px', fontWeight: 600 }}>Status</h3>
        </div>
        <div style={{ display: 'flex' }}>
          {STATUSES.filter(s => s !== 'cancelled').map((s, i, arr) => {
            const currentOrder = STATUS_ORDER[order.status]
            const isDone = order.status !== 'cancelled' && STATUS_ORDER[s] <= currentOrder
            const isCurrent = s === order.status
            return (
              <div key={s} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                {i > 0 && <div style={{ position: 'absolute', left: 0, top: '12px', right: '50%', height: '2px', background: isDone ? 'var(--accent)' : 'var(--border-2)' }} />}
                {i < arr.length - 1 && <div style={{ position: 'absolute', right: 0, top: '12px', left: '50%', height: '2px', background: isDone && !isCurrent ? 'var(--accent)' : 'var(--border-2)' }} />}
                <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: isDone ? 'var(--accent)' : 'var(--bg-4)', border: `2px solid ${isDone ? 'var(--accent)' : 'var(--border-2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, fontSize: '10px', color: isDone ? 'white' : 'var(--text-3)', fontWeight: 700 }}>
                  {isDone ? '✓' : i + 1}
                </div>
                <div style={{ fontSize: '10px', fontWeight: isCurrent ? 600 : 400, color: isCurrent ? 'var(--accent)' : isDone ? 'var(--text-2)' : 'var(--text-3)', marginTop: '6px', textAlign: 'center', textTransform: 'capitalize' }}>{s}</div>
              </div>
            )
          })}
        </div>
        {order.status === 'cancelled' && <div className="alert alert-danger" style={{ marginTop: '12px' }}>This order was cancelled.</div>}
      </div>

      {/* Line Items */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600 }}>Line Items</h3>
          <button className="btn btn-primary btn-sm" onClick={() => setLineModal(true)}><Plus size={13} />Add Line</button>
        </div>
        {lines.length === 0 ? <div className="empty-state" style={{ padding: '30px' }}><p>No line items yet</p></div> : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>SKU</th><th>Product</th><th>Qty</th><th>Unit Price</th><th>Line Total</th><th title="Whether these units were already removed from Shopify's available count">Shopify Deducted</th><th>Fulfilled</th><th></th></tr></thead>
              <tbody>
                {lines.map(l => (
                  <tr key={l.id}>
                    <td><span className="mono">{l.products?.sku || '—'}</span></td>
                    <td>{l.products?.name || l.notes || '—'}</td>
                    <td>{l.quantity}</td>
                    <td>{l.unit_price != null ? `$${Number(l.unit_price).toFixed(2)}` : '—'}</td>
                    <td>{l.unit_price != null ? `$${(l.quantity * Number(l.unit_price)).toFixed(2)}` : '—'}</td>
                    <td>
                      <button
                        className={`badge badge-${l.shopify_already_deducted ? 'success' : 'warning'}`}
                        style={{ cursor: 'pointer', border: 'none' }}
                        title="Click to toggle"
                        onClick={() => updateLine(l.id, { shopify_already_deducted: !l.shopify_already_deducted })}
                      >{l.shopify_already_deducted ? 'Yes' : 'No'}</button>
                    </td>
                    <td>
                      <input
                        className="input"
                        type="number"
                        style={{ width: '70px', padding: '4px 6px', fontSize: '12px' }}
                        value={l.fulfilled_quantity || 0}
                        onChange={e => updateLine(l.id, { fulfilled_quantity: parseInt(e.target.value, 10) || 0 })}
                      />
                    </td>
                    <td><button className="btn btn-ghost btn-sm" onClick={() => deleteLine(l.id)}><Trash2 size={13} style={{ color: 'var(--danger)' }} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {lineModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setLineModal(false)}>
          <div className="modal">
            <div className="modal-header"><h2>Add Line Item</h2><button className="btn btn-ghost btn-sm" onClick={() => setLineModal(false)}><X size={16} /></button></div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Product</label>
                <ProductPicker products={products} value={lineForm.product_id} onChange={handleProductSelect} placeholder="Search for a product..." />
              </div>
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Quantity *</label><input className="input" type="number" value={lineForm.quantity} onChange={e => setLineForm(f => ({ ...f, quantity: e.target.value }))} placeholder="0" /></div>
                <div className="form-group"><label className="form-label">Unit Price</label><input className="input" type="number" step="0.01" value={lineForm.unit_price} onChange={e => setLineForm(f => ({ ...f, unit_price: e.target.value }))} placeholder="0.00" /></div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={lineForm.shopify_already_deducted} onChange={e => setLineForm(f => ({ ...f, shopify_already_deducted: e.target.checked }))} />
                Already removed from Shopify's available inventory
              </label>
              <p style={{ fontSize: '11px', color: 'var(--text-3)', margin: '4px 0 0' }}>Leave checked if these units were pulled from Shopify up front. Uncheck only if they're still in Shopify's sellable count — those units count as "Allocated to Wholesale" on the Inventory page.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setLineModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addLine} disabled={saving || !lineForm.quantity}>{saving ? 'Adding...' : 'Add Line'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
